import { useState, useEffect } from 'react';
import { normalizeGamesBySport } from '../utils/gameNormalizer';
import { isGameFinished } from '../utils/constants';

const sortGamesByStatus = (games) => {
  return [...games].sort((a, b) => {
    // Move games with status 'Final' or 'Game Over' to the bottom
    if (isGameFinished(a.status) && !isGameFinished(b.status)) {
      return 1;
    }
    if (!isGameFinished(a.status) && isGameFinished(b.status)) {
      return -1;
    }
    // Keep original order for non-finished games
    return 0;
  });
};

export const useAnalyticsGamesData = (selectedDate = null, sport = 'mlb', analyticsMode = false) => {
  const [games, setGames] = useState([]);
  const [gamesMetadata, setGamesMetadata] = useState(null);

  const fetchGamesMeta = async (date = null) => {
    try {
      const dateParam = date || selectedDate;
      let endpoint = `/api/games/${sport}/today`;
      
      if (dateParam) {
        // Convert MM/DD/YYYY to YYYY-MM-DD format for API
        const parts = dateParam.split('/');
        const apiDate = `${parts[2]}-${parts[0].padStart(2, '0')}-${parts[1].padStart(2, '0')}`;
        endpoint = `/api/games/${sport}/${apiDate}`;
      }
      
      let response = await fetch(endpoint);

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      let data = await response.json();

      // MLB fallback: if current date returns no games, try /next
      // This handles off-season dates where /today (or current date) has no games
      if (sport === 'mlb') {
        const hasGames = Array.isArray(data) ? data.length > 0 : (data?.games?.length > 0);

        if (!hasGames) {
          console.log('Analytics: MLB returned no games for date, trying /next');
          const nextEndpoint = `/api/games/mlb/next`;
          const nextResponse = await fetch(nextEndpoint);

          if (nextResponse.ok) {
            data = await nextResponse.json();
          }
        }
      }

      // Handle different API response formats
      if (Array.isArray(data)) {
        // Legacy format: direct array (MLB)
        console.log('Analytics: Using legacy array format');
        const sortedGames = sortGamesByStatus(data);
        setGames(sortedGames);
        setGamesMetadata({
          sport: sport,
          totalGames: data.length,
          format: 'legacy'
        });
      } else if (data && typeof data === 'object' && data.games && Array.isArray(data.games)) {
        // New format: object with games array
        console.log('Analytics: Using new object format for', data.sport || sport);
        
        // Normalize games based on sport type
        const normalizedGames = normalizeGamesBySport(data.games, data.sport || sport);
        const sortedGames = sortGamesByStatus(normalizedGames);
        
        setGames(sortedGames);
        setGamesMetadata({
          sport: data.sport,
          sportDisplayName: data.sportDisplayName,
          date: data.date,
          gameDateKey: data.gameDateKey,
          totalGames: data.totalGames,
          timestamp: data.timestamp,
          format: 'new'
        });
      } else if (data && data.error) {
        // Error response
        console.error('Analytics API returned error:', data.error);
        throw new Error(data.error);
      } else {
        // Unexpected format
        console.error('Analytics: Unexpected API response format:', data);
        throw new Error('Unexpected response format');
      }
      
    } catch (err) {
      console.error('Analytics: Failed to fetch games:', err);
      setGames([]); // Set to empty array on error
      setGamesMetadata({
        sport: sport,
        totalGames: 0,
        error: err.message,
        format: 'error'
      });
    }
  };

  useEffect(() => {
    // Only fetch if analytics mode is enabled
    if (analyticsMode) {
      console.log('Analytics: Fetching games once for analytics mode');
      fetchGamesMeta();
      // NO interval - fetch only once
    }
  }, [selectedDate, sport, analyticsMode]); // Only refetch when these change

  return { analyticsGames: games, analyticsGamesMetadata: gamesMetadata };
};